/**
 * One-hand and two-hand manipulation, as pure maths on plain vectors.
 *
 * Kept engine-free so the feel can be tested without a headset: every clamp and
 * every pivot choice below is a unit test rather than something only a person
 * in a Quest can check. The bridge converts to and from three's types.
 *
 * Two hands give three degrees of freedom at once, and the rule that makes it
 * feel right is that the body pivots about the point between the hands, not
 * about its own origin. Scale about the midpoint, rotate about the midpoint,
 * and translate with the midpoint; the grabbed point then stays under the hands.
 */

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/** Where the two grabbing hands are. */
export interface TwoHandPose {
  a: Vec3;
  b: Vec3;
}

/** A body's transform, restricted to what manipulation may change. */
export interface BodyTransform {
  position: Vec3;
  /** Uniform scale; anatomy is never stretched on one axis. */
  scale: number;
  /** Rotation about world up, in radians. */
  yaw: number;
}

export interface ManipulationLimits {
  minScale: number;
  maxScale: number;
  /**
   * Comfort option: ignore the hands' roll and pitch and turn about world up
   * only. On by default because free rotation of a life-size body is a common
   * source of discomfort.
   */
  yawOnly: boolean;
}

export const DEFAULT_LIMITS: ManipulationLimits = { minScale: 0.05, maxScale: 3, yawOnly: true };

const sub = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const add = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z });
const scaled = (a: Vec3, k: number): Vec3 => ({ x: a.x * k, y: a.y * k, z: a.z * k });
const mid = (a: Vec3, b: Vec3): Vec3 => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, z: (a.z + b.z) / 2 });
const length = (a: Vec3): number => Math.hypot(a.x, a.y, a.z);

/** Rotate about world up (the Y axis). */
export function rotateY(v: Vec3, radians: number): Vec3 {
  const c = Math.cos(radians);
  const s = Math.sin(radians);
  return { x: v.x * c + v.z * s, y: v.y, z: -v.x * s + v.z * c };
}

/** Signed angle from `from` to `to` about world up, in (-pi, pi]. */
export function yawBetween(from: Vec3, to: Vec3): number {
  const a = Math.atan2(from.x, from.z);
  const b = Math.atan2(to.x, to.z);
  let d = a - b;
  while (d > Math.PI) d -= 2 * Math.PI;
  while (d <= -Math.PI) d += 2 * Math.PI;
  return d;
}

/** Move with one hand: the body follows the hand, unrotated and unscaled. */
export function oneHandTransform(start: Vec3, now: Vec3, base: BodyTransform): BodyTransform {
  return { ...base, position: add(base.position, sub(now, start)) };
}

/**
 * Move, scale and turn with two hands.
 *
 * The scale factor is clamped first, and the clamped factor is what moves the
 * body. Clamping the result instead would let the body drift out from under the
 * hands once a limit is reached, which reads as the model sliding away.
 */
export function twoHandTransform(start: TwoHandPose, now: TwoHandPose, base: BodyTransform, limits: ManipulationLimits = DEFAULT_LIMITS): BodyTransform {
  const startSpan = sub(start.b, start.a);
  const nowSpan = sub(now.b, now.a);
  const startDistance = length(startSpan);

  // Hands together: no usable span to scale or turn by, so translate only.
  let factor = 1;
  if (startDistance > 1e-4) {
    const wanted = base.scale * (length(nowSpan) / startDistance);
    factor = Math.min(limits.maxScale, Math.max(limits.minScale, wanted)) / base.scale;
  }

  const yaw = startDistance > 1e-4 ? yawBetween(startSpan, nowSpan) : 0;
  const startMid = mid(start.a, start.b);
  const nowMid = mid(now.a, now.b);

  // The body's offset from the pivot, scaled and turned, then re-anchored to
  // wherever the hands have moved the pivot to.
  const offset = rotateY(scaled(sub(base.position, startMid), factor), yaw);
  // `BodyTransform` carries yaw alone, so this is the yaw-only path either way.
  // `limits.yawOnly` is what the bridge reads to decide whether to offer free
  // rotation at all; turning it off is a Phase 2 comfort setting, not a
  // different sum here.
  return { position: add(nowMid, offset), scale: base.scale * factor, yaw: base.yaw + yaw };
}

/**
 * Scale about a chosen point rather than the body's origin: what the controls
 * dock's zoom buttons do, keeping whatever the user is looking at in place.
 */
export function scaleAbout(base: BodyTransform, pivot: Vec3, factor: number, limits: ManipulationLimits = DEFAULT_LIMITS): BodyTransform {
  const wanted = Math.min(limits.maxScale, Math.max(limits.minScale, base.scale * factor));
  const applied = wanted / base.scale;
  return { ...base, scale: wanted, position: add(pivot, scaled(sub(base.position, pivot), applied)) };
}

/** Turn about a chosen point, for the dock's rotate buttons. */
export function yawAbout(base: BodyTransform, pivot: Vec3, radians: number): BodyTransform {
  return { ...base, yaw: base.yaw + radians, position: add(pivot, rotateY(sub(base.position, pivot), radians)) };
}

/**
 * Put the body an arm's length in front of the viewer at a readable size, which
 * is both the initial pose and what *reset* and *bring to me* do.
 */
export function framedInFront(viewer: Vec3, forward: Vec3, distance: number, scale: number): BodyTransform {
  const flat = { x: forward.x, y: 0, z: forward.z };
  const len = Math.hypot(flat.x, flat.z);
  const dir = len > 1e-4 ? { x: flat.x / len, y: 0, z: flat.z / len } : { x: 0, y: 0, z: -1 };
  return {
    position: { x: viewer.x + dir.x * distance, y: 0, z: viewer.z + dir.z * distance },
    scale,
    // face the viewer
    yaw: Math.atan2(-dir.x, -dir.z),
  };
}
